import { FC, useCallback, useMemo } from 'react';
import { useForm } from 'react-final-form';

import InstabookOption from 'src/book-now-direct/components/InstabookOption';
import {
  CAPTAINED, DEPARTURE_DATE_FIELD, DEPARTURE_TIME_FIELD, DURATION_FIELD,
  type BookNowDirectFormState,
} from 'src/book-now-direct/constants';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import BookingMotivator from 'src/common/components/BookingMotivator';
import type { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import newTZDate, { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import Typography from 'src/common/components/Typography';
import { BOOK_NOW_DATE_FORMAT } from 'src/common/constants';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import { filterInstabooks } from 'src/book-now-direct/helpers';
import { useTranslation } from 'next-i18next';
import type { Motivator } from 'src/common/types/api/v4/bookingMotivators';
import styles from './InstabookOptionsList.module.scss';

type Props = {
  closeModal: () => void;
  selectedMotivators: Motivator[];
  daysUntilInstabook?: number;
};

const InstabookOptionsList: FC<Props> = ({
  closeModal,
  selectedMotivators,
  daysUntilInstabook,
}) => {
  const { change, batch, getState } = useForm<BookNowDirectFormState>();
  const { values } = getState();
  const { departDate } = values;
  const { t } = useTranslation('book-now-direct');

  const {
    previewInstabooksOnDate,
    loading,
    commitPreviewInstabooks,
    blockedTimes,
  } = useBookNowDirectContext();

  /** If a specific instabook card is clicked, prefill some values */
  const onClickInstabookCard = useCallback((instabook: InstabookAvailability) => {
    batch(() => {
      change(DEPARTURE_DATE_FIELD, newTZDate(instabook.date));
      change(DURATION_FIELD, instabook.trip_length);
      change(CAPTAINED, instabook.captained);
      change(DEPARTURE_TIME_FIELD, instabook.instabook_trip_times[0].min_start_time);
    });
    commitPreviewInstabooks();
    closeModal();
  }, [batch, change, closeModal, commitPreviewInstabooks]);

  const selectedInstabook = useMemo(
    () => filterInstabooks(previewInstabooksOnDate, values)?.[0],
    [previewInstabooksOnDate, values],
  );
  const dateFnsLocale = useDateFnsLocale();

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        {previewInstabooksOnDate?.[0] && (
          <Typography
            size="body-xl"
            weight="semibold"
            type="p"
          >
            {`${t('options-for')} ${formatDate(
              // Ensure we always have a valid Date object
              previewInstabooksOnDate[0]?.date
                ? newTZDate(previewInstabooksOnDate[0].date)
                : (departDate || todayDate()),
              BOOK_NOW_DATE_FORMAT,
              dateFnsLocale,
            )}`}
          </Typography>
        )}
      </div>
      {loading && (
        <div className={styles.loading}>
          <LoadingSpinner background />
        </div>
      )}
      {selectedMotivators && selectedMotivators.length > 0 && (
        <BookingMotivator
          motivators={selectedMotivators}
          daysUntilInstabook={daysUntilInstabook}
          visibleBlockedTimeCount={blockedTimes.length}
          blockedTimes={blockedTimes}
          showBlockTimeDescription={false}
        />
      )}
      {previewInstabooksOnDate?.map((instabook) => (
        <InstabookOption
          instabook={instabook}
          key={instabook.id}
          onClick={() => onClickInstabookCard(instabook)}
          selectedInstabookId={selectedInstabook?.id}
        />
      ))}
    </div>
  );
};

export default InstabookOptionsList;
